<template>
  <div class="review-container">
    <div class="review-panel">
      <div class="back-link">
        <router-link :to="isEditMode ? '/curator/gene_symbol_pending?tab=reviewed' : '/curator/gene_symbol_pending'">
          &larr; {{ isEditMode ? 'Back to Reviewed Symbols' : 'Back to Pending Symbols' }}
        </router-link>
      </div>

      <h2>{{ isEditMode ? 'Edit Gene Symbol' : 'Gene Symbol Review' }}</h2>

      <div v-if="loading" class="loading-message">
        Loading symbol details...
      </div>

      <div v-else-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>

      <div v-else-if="symbol" class="review-content">
        <!-- Symbol Details Section -->
        <div class="section">
          <h3>Symbol Details</h3>
          <table class="detail-table">
            <tr>
              <td class="label">Symbol:</td>
              <td class="value" v-if="isEditMode">
                <input type="text" v-model="editForm.symbol" class="edit-input" />
              </td>
              <td class="value symbol-value" v-else>{{ symbol.SYMBOL }}</td>
            </tr>
            <tr>
              <td class="label">Full Name:</td>
              <td class="value" v-if="isEditMode">
                <input type="text" v-model="editForm.descriptiveSymbol" class="edit-input" />
              </td>
              <td class="value" v-else>{{ symbol.DESCRIPTIVE_SYMBOL || "—" }}</td>
            </tr>
            <tr>
              <td class="label">Type:</td>
              <td class="value" v-if="isEditMode">
                <select v-model="editForm.hasMutantPhenotype" class="edit-select">
                  <option value="F">Gene Product Symbol</option>
                  <option value="T">Mutant Phenotype Symbol</option>
                </select>
              </td>
              <td class="value" v-else>
                {{
                  symbol.HAS_MUTANT_PHENOTYPE === "T"
                    ? "Mutant Phenotype Symbol"
                    : "Gene Product Symbol"
                }}
              </td>
            </tr>
            <tr>
              <td class="label">Phenotype Class:</td>
              <td class="value" v-if="isEditMode">
                <input type="text" v-model="editForm.mutantPhenotypeClass" class="edit-input" />
              </td>
              <td class="value" v-else>{{ symbol.MUTANT_PHENOTYPE_CLASS || "—" }}</td>
            </tr>
            <tr>
              <td class="label">Associated Loci Count:</td>
              <td class="value" v-if="isEditMode">
                <select v-model="editForm.associatedLociCount" class="edit-select">
                  <option value="single_locus">1 Gene</option>
                  <option value="multiple_loci">&gt; 1 Gene</option>
                  <option value="number_of_loci_unknown">Unknown</option>
                </select>
              </td>
              <td class="value" v-else>
                {{ formatLociCount(symbol.ASSOCIATED_LOCI_COUNT) }}
              </td>
            </tr>
            <tr>
              <td class="label">Description:</td>
              <td class="value" v-if="isEditMode">
                <textarea v-model="editForm.description" rows="3" class="edit-textarea"></textarea>
              </td>
              <td class="value" v-else>{{ symbol.DESCRIPTION || "—" }}</td>
            </tr>
            <tr>
              <td class="label">Current Status:</td>
              <td class="value" v-if="isEditMode">
                <select v-model="editForm.status" class="edit-select">
                  <option value="approved">Approved</option>
                  <option value="obsolete">Obsolete</option>
                  <option value="pending">Pending</option>
                </select>
              </td>
              <td class="value" v-else>
                <span
                  :class="[
                    'status-badge',
                    symbol.STATUS === 'await_approval'
                      ? 'status-new'
                      : 'status-' + symbol.STATUS,
                  ]"
                >
                  {{
                    symbol.STATUS === "await_approval" ? "Awaiting Approval" : symbol.STATUS
                  }}
                </span>
              </td>
            </tr>
            <tr>
              <td class="label">Submitter:</td>
              <td class="value">{{ symbol.SUBMITTER_INFO || "—" }}</td>
            </tr>
            <tr>
              <td class="label">Submitted:</td>
              <td class="value">
                {{ formatDate(symbol.RESERVATION_DATE) }}
              </td>
            </tr>
            <tr>
              <td class="label">Submitter Comments:</td>
              <td class="value" v-if="isEditMode">
                <textarea v-model="editForm.comments" rows="3" class="edit-textarea"></textarea>
              </td>
              <td class="value comments-text" v-else>{{ symbol.COMMENTS || "—" }}</td>
            </tr>
          </table>
        </div>

        <!-- Linked Loci Section -->
        <div class="section">
          <h3>Associated Loci</h3>
          <div v-if="linkedLoci.length > 0" class="linked-loci">
            <div
              v-for="(locus, index) in linkedLoci"
              :key="index"
              class="locus-tag"
            >
              {{ locus.LOCUS_NAME || locus.locusName }}
              <span v-if="locus.REP_GENE_MODEL || locus.repGeneModel">
                ({{ locus.REP_GENE_MODEL || locus.repGeneModel }})
              </span>
              <span class="remove-locus" @click="removeLocus(index)"
                >&times;</span
              >
            </div>
          </div>
          <p v-else class="no-loci">No loci currently linked.</p>

          <!-- Search and link loci -->
          <div class="locus-search">
            <label for="locusSearch">Link a Locus:</label>
            <input
              type="text"
              id="locusSearch"
              v-model="locusSearchQuery"
              @input="fetchLocusResults"
              placeholder="Search by locus name or AGI ID"
            />
            <ul class="suggestions-dropdown" v-if="locusSearchResults.length">
              <li
                v-for="result in locusSearchResults"
                :key="result.LOCUS_ID"
                @click="selectLocus(result)"
              >
                {{ result.LOCUS_NAME }}
                <span v-if="result.REP_GENE_MODEL">
                  ({{ result.REP_GENE_MODEL }})
                </span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Associated Publications Section -->
        <div class="section">
          <h3>Associated Publications</h3>
          <div v-if="linkedPublications.length > 0" class="linked-loci">
            <div
              v-for="(pub, index) in linkedPublications"
              :key="index"
              class="locus-tag pub-tag"
            >
              {{ pub.title }}
              <span v-if="pub.referenceId">
                (Reference ID: {{ pub.referenceId }})
              </span>
              <span class="remove-locus" @click="removePublication(index)"
                >&times;</span
              >
            </div>
          </div>
          <p v-else class="no-loci">No publications currently linked.</p>

          <!-- Search and link publications -->
          <div class="locus-search">
            <label for="pubSearch">Link a Publication:</label>
            <input
              type="text"
              id="pubSearch"
              v-model="pubSearchQuery"
              @input="fetchPubResults"
              placeholder="Search by title, DOI or PubMed ID"
            />
            <ul class="suggestions-dropdown" v-if="pubSearchResults.length">
              <li
                v-for="result in pubSearchResults"
                :key="result.REFERENCE_ID"
                @click="selectPublication(result)"
              >
                {{ result.TITLE }}
                <span v-if="result.PUBMED_ID">
                  (PubMed: {{ result.PUBMED_ID }})
                </span>
                <span v-else-if="result.DOI">
                  (DOI: {{ result.DOI }})
                </span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Curator Decision Section (Review mode only) -->
        <div v-if="!isEditMode" class="section decision-section">
          <h3>Curator Decision</h3>

          <div class="decision-options">
            <label class="decision-option">
              <input
                type="radio"
                v-model="approvalAction"
                value="approved"
              />
              <span class="option-label approve-label">Approve</span>
            </label>
            <label class="decision-option">
              <input
                type="radio"
                v-model="approvalAction"
                value="pending"
              />
              <span class="option-label pending-label">Pending</span>
            </label>
            <label class="decision-option">
              <input
                type="radio"
                v-model="approvalAction"
                value="obsolete"
              />
              <span class="option-label obsolete-label">Obsolete</span>
            </label>
          </div>

          <div class="input-group">
            <label for="reason">
              Comment / Reason:
              <span
                v-if="
                  approvalAction === 'pending' ||
                  approvalAction === 'obsolete'
                "
                class="required"
                >*</span
              >
            </label>
            <textarea
              id="reason"
              v-model="reason"
              rows="4"
              placeholder="Enter reason for decision (required for Pending/Obsolete)"
            ></textarea>
          </div>

          <div class="email-option">
            <label>
              <input type="checkbox" v-model="sendEmail" />
              Send email notification to submitter
            </label>
          </div>

          <div class="action-buttons">
            <button
              class="submit-decision-btn"
              :disabled="!canSubmit || submitting"
              @click="submitDecision"
            >
              {{ submitting ? "Submitting..." : "Submit Decision" }}
            </button>
          </div>

          <p v-if="successMessage" class="success-message">
            {{ successMessage }}
          </p>
          <p v-if="decisionError" class="error-message">
            {{ decisionError }}
          </p>
        </div>

        <!-- Save Changes Section (Edit mode only) -->
        <div v-if="isEditMode" class="section decision-section">
          <h3>Save Changes</h3>
          <p class="edit-note">
            Changes will be reflected on the browse page after the next Sunday pipeline run.
          </p>
          <div class="action-buttons">
            <button
              class="submit-decision-btn save-btn"
              :disabled="saving"
              @click="saveEdits"
            >
              {{ saving ? "Saving..." : "Save Changes" }}
            </button>
            <button
              class="cancel-btn"
              @click="cancelEdit"
              :disabled="saving"
            >
              Cancel
            </button>
          </div>

          <p v-if="saveSuccess" class="success-message">
            {{ saveSuccess }}
          </p>
          <p v-if="saveError" class="error-message">
            {{ saveError }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Api from "@/services/Api";
import { mapGetters } from "vuex";

export default {
  name: "GeneSymbolReview",
  data() {
    return {
      symbol: null,
      linkedLoci: [],
      loading: true,
      errorMessage: "",
      // Review mode
      approvalAction: "",
      reason: "",
      sendEmail: true,
      submitting: false,
      successMessage: "",
      decisionError: "",
      // Loci search
      locusSearchQuery: "",
      locusSearchResults: [],
      newLoci: [],
      // Publications (parsed from comments text)
      linkedPublications: [],
      pubSearchQuery: "",
      pubSearchResults: [],
      // Edit mode
      isEditMode: false,
      editForm: {
        symbol: "",
        descriptiveSymbol: "",
        hasMutantPhenotype: "F",
        mutantPhenotypeClass: "",
        associatedLociCount: "",
        description: "",
        comments: "",
        status: "",
      },
      saving: false,
      saveSuccess: "",
      saveError: "",
    };
  },
  computed: {
    ...mapGetters({
      getCommunityId: "authProfile/getCommunityId",
    }),
    canSubmit() {
      if (!this.approvalAction) return false;
      if (
        (this.approvalAction === "pending" ||
          this.approvalAction === "obsolete") &&
        !this.reason.trim()
      ) {
        return false;
      }
      return true;
    },
  },
  async created() {
    const tairObjectId = this.$route.query.id;
    const mode = this.$route.query.mode;
    this.isEditMode = mode === "edit";

    if (!tairObjectId) {
      this.errorMessage = "No symbol ID provided.";
      this.loading = false;
      return;
    }
    await this.fetchSymbolDetails(tairObjectId);
  },
  methods: {
    async fetchSymbolDetails(tairObjectId) {
      this.loading = true;
      try {
        const communityId = this.getCommunityId;
        const response = await Api().get(
          `gene_class_symbol/review/${tairObjectId}`,
          { params: { communityId } }
        );
        if (response.data) {
          this.symbol = response.data.symbol;
          this.linkedLoci = response.data.loci || [];

          // Parse publications from comments text
          this.linkedPublications = this.parsePublicationsFromComments(
            this.symbol.COMMENTS || ""
          );

          // Populate edit form if in edit mode
          if (this.isEditMode && this.symbol) {
            this.editForm.symbol = this.symbol.SYMBOL || "";
            this.editForm.descriptiveSymbol = this.symbol.DESCRIPTIVE_SYMBOL || "";
            this.editForm.hasMutantPhenotype = this.symbol.HAS_MUTANT_PHENOTYPE || "F";
            this.editForm.mutantPhenotypeClass = this.symbol.MUTANT_PHENOTYPE_CLASS || "";
            this.editForm.associatedLociCount = this.symbol.ASSOCIATED_LOCI_COUNT || "";
            this.editForm.description = this.symbol.DESCRIPTION || "";
            // Strip "Associated Publications:" line from comments for the edit textarea
            // since publications are managed via the separate UI
            this.editForm.comments = (this.symbol.COMMENTS || "")
              .replace(/\n?\s*Associated Publications:.*$/s, "")
              .trimEnd();
            this.editForm.status = this.symbol.STATUS || "approved";
          }
        }
      } catch (error) {
        console.error("Error fetching symbol details:", error);
        this.errorMessage =
          error.response && error.response.status === 403
            ? "Access denied. Curator access required."
            : "Error loading symbol details.";
      } finally {
        this.loading = false;
      }
    },
    async fetchLocusResults() {
      if (this.locusSearchQuery.length < 2) {
        this.locusSearchResults = [];
        return;
      }
      try {
        const response = await Api().get("gene_class_symbol/locus-search", {
          params: { searchTerm: this.locusSearchQuery },
        });
        if (response.data && response.data.rows) {
          this.locusSearchResults = response.data.rows;
        }
      } catch (error) {
        console.error("Error searching loci:", error);
        this.locusSearchResults = [];
      }
    },
    selectLocus(locus) {
      const alreadyLinked = this.linkedLoci.some(
        (l) => (l.LOCUS_ID || l.locusId) === locus.LOCUS_ID
      );
      if (!alreadyLinked) {
        this.linkedLoci.push({
          locusId: locus.LOCUS_ID,
          locusName: locus.LOCUS_NAME,
          repGeneModel: locus.REP_GENE_MODEL,
          isNew: true,
        });
        this.newLoci.push({
          locusId: locus.LOCUS_ID,
          locusName: locus.LOCUS_NAME,
        });
      }
      this.locusSearchQuery = "";
      this.locusSearchResults = [];
    },
    removeLocus(index) {
      const removed = this.linkedLoci[index];
      this.linkedLoci.splice(index, 1);
      if (removed.isNew) {
        const newIdx = this.newLoci.findIndex(
          (l) => l.locusId === removed.locusId
        );
        if (newIdx !== -1) this.newLoci.splice(newIdx, 1);
      }
    },
    parsePublicationsFromComments(comments) {
      // Parse "Associated Publications: Title (Reference ID: X), Title2 (Reference ID: Y)"
      // Uses a global regex anchored on "(Reference ID: N)" to avoid breaking on
      // titles that contain parentheses or commas.
      const pubs = [];
      const pubMatch = comments.match(/Associated Publications:\s*([\s\S]*)/);
      if (pubMatch && pubMatch[1]) {
        const pubText = pubMatch[1].trim();
        const re = /(.+?)\s*\(Reference ID:\s*(\d+)\)/g;
        let m;
        while ((m = re.exec(pubText)) !== null) {
          // Strip leading ", " from title (separator between entries)
          const title = m[1].replace(/^,\s*/, "").trim();
          if (title) {
            pubs.push({
              title: title,
              referenceId: parseInt(m[2], 10),
            });
          }
        }
      }
      return pubs;
    },
    buildCommentsWithPublications(baseComments, publications) {
      // Remove existing "Associated Publications:" line from comments
      let cleaned = baseComments.replace(/\n?\s*Associated Publications:.*$/s, "").trimEnd();
      if (publications.length > 0) {
        const pubText = publications
          .map((p) => `${p.title} (Reference ID: ${p.referenceId})`)
          .join(", ");
        cleaned += `\n        Associated Publications: ${pubText}`;
      }
      return cleaned;
    },
    async fetchPubResults() {
      if (this.pubSearchQuery.length < 2) {
        this.pubSearchResults = [];
        return;
      }
      try {
        const response = await Api().get(
          "gene_class_symbol/publication-search",
          { params: { searchTerm: this.pubSearchQuery } }
        );
        if (response.data && response.data.rows) {
          this.pubSearchResults = response.data.rows;
        }
      } catch (error) {
        console.error("Error searching publications:", error);
        this.pubSearchResults = [];
      }
    },
    selectPublication(pub) {
      const alreadyLinked = this.linkedPublications.some(
        (p) => p.referenceId === pub.REFERENCE_ID
      );
      if (!alreadyLinked) {
        this.linkedPublications.push({
          referenceId: pub.REFERENCE_ID,
          title: pub.TITLE,
        });
      }
      this.pubSearchQuery = "";
      this.pubSearchResults = [];
    },
    removePublication(index) {
      this.linkedPublications.splice(index, 1);
    },
    async submitDecision() {
      this.submitting = true;
      this.successMessage = "";
      this.decisionError = "";

      try {
        const communityId = this.getCommunityId;
        const response = await Api().post("gene_class_symbol/approve", {
          communityId,
          tairObjectId: this.symbol.TAIR_OBJECT_ID,
          approvalAction: this.approvalAction,
          reason: this.reason,
          sendEmail: this.sendEmail,
          loci: this.newLoci,
        });

        if (response.data && response.data.message) {
          this.successMessage = response.data.message;
          setTimeout(() => {
            this.$router.push("/curator/gene_symbol_pending");
          }, 3000);
        }
      } catch (error) {
        console.error("Error submitting decision:", error);
        this.decisionError =
          error.response && error.response.data && error.response.data.error
            ? error.response.data.error
            : "Error submitting decision. Please try again.";
      } finally {
        this.submitting = false;
      }
    },
    async saveEdits() {
      this.saving = true;
      this.saveSuccess = "";
      this.saveError = "";

      try {
        const communityId = this.getCommunityId;

        // Build full loci array from linkedLoci (both existing and new)
        const allLoci = this.linkedLoci.map((l) => ({
          locusId: l.LOCUS_ID || l.locusId,
          locusName: l.LOCUS_NAME || l.locusName,
        }));

        // Rebuild comments with updated publications
        const updatedComments = this.buildCommentsWithPublications(
          this.editForm.comments,
          this.linkedPublications
        );

        const response = await Api().post("gene_class_symbol/update", {
          communityId,
          tairObjectId: this.symbol.TAIR_OBJECT_ID,
          symbol: this.editForm.symbol,
          descriptiveSymbol: this.editForm.descriptiveSymbol,
          hasMutantPhenotype: this.editForm.hasMutantPhenotype,
          mutantPhenotypeClass: this.editForm.mutantPhenotypeClass,
          associatedLociCount: this.editForm.associatedLociCount,
          description: this.editForm.description,
          comments: updatedComments,
          status: this.editForm.status,
          loci: allLoci,
        });

        if (response.data && response.data.message) {
          this.saveSuccess = response.data.message;
          setTimeout(() => {
            this.$router.push("/curator/gene_symbol_pending?tab=reviewed");
          }, 2000);
        }
      } catch (error) {
        console.error("Error saving edits:", error);
        this.saveError =
          error.response && error.response.data && error.response.data.error
            ? error.response.data.error
            : "Error saving changes. Please try again.";
      } finally {
        this.saving = false;
      }
    },
    cancelEdit() {
      this.$router.push("/curator/gene_symbol_pending?tab=reviewed");
    },
    formatDate(dateStr) {
      if (!dateStr) return "—";
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    formatLociCount(count) {
      switch (count) {
        case "single_locus":
          return "1 Gene";
        case "multiple_loci":
          return "> 1 Gene";
        case "number_of_loci_unknown":
          return "Unknown";
        default:
          return count || "—";
      }
    },
  },
};
</script>

<style scoped>
.review-container {
  display: flex;
  flex-direction: column;
  padding: 20px;
  box-sizing: border-box;
}

.review-panel {
  border: 1px solid #ccc;
  padding: 20px;
  margin: 0 auto;
  width: 100%;
  max-width: 900px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background: #fff;
}

.back-link {
  margin-bottom: 15px;
}

.back-link a {
  color: #007bff;
  text-decoration: none;
}

.back-link a:hover {
  text-decoration: underline;
}

.section {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.section:last-child {
  border-bottom: none;
}

.section h3 {
  color: #333;
  margin-bottom: 15px;
  padding-bottom: 5px;
  border-bottom: 2px solid #007bff;
  display: inline-block;
}

.detail-table {
  width: 100%;
}

.detail-table tr {
  border-bottom: 1px solid #f0f0f0;
}

.detail-table td {
  padding: 8px 12px;
  vertical-align: top;
}

.detail-table .label {
  font-weight: bold;
  width: 200px;
  color: #555;
}

.detail-table .value {
  color: #333;
}

.symbol-value {
  font-family: monospace;
  font-size: 1.2em;
  font-weight: bold;
}

.comments-text {
  white-space: pre-wrap;
  font-size: 0.95em;
}

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.85em;
  font-weight: bold;
  text-transform: capitalize;
}

.status-new {
  background-color: #fff3cd;
  color: #856404;
}

.status-pending {
  background-color: #cce5ff;
  color: #004085;
}

.status-approved {
  background-color: #d4edda;
  color: #155724;
}

.status-obsolete {
  background-color: #f5c6cb;
  color: #721c24;
}

/* Edit mode inputs */
.edit-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.95em;
  box-sizing: border-box;
}

.edit-select {
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.95em;
}

.edit-textarea {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.95em;
  font-family: inherit;
  box-sizing: border-box;
}

.edit-note {
  color: #666;
  font-size: 0.9em;
  margin-bottom: 15px;
  font-style: italic;
}

.linked-loci {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 15px;
}

.locus-tag {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  background-color: #e8f4e8;
  border: 1px solid #b8d4b8;
  border-radius: 20px;
  font-size: 0.9em;
}

.pub-tag {
  background-color: #e8ecf4;
  border-color: #b8c4d4;
}

.remove-locus {
  margin-left: 8px;
  cursor: pointer;
  color: #dc3545;
  font-size: 1.2em;
  font-weight: bold;
}

.remove-locus:hover {
  color: #a71d2a;
}

.no-loci {
  color: #888;
  font-style: italic;
}

.locus-search {
  margin-top: 10px;
  position: relative;
}

.locus-search label {
  display: block;
  font-weight: bold;
  margin-bottom: 5px;
}

.locus-search input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}

.suggestions-dropdown {
  position: absolute;
  z-index: 1000;
  width: 100%;
  background: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-top: none;
  list-style: none;
  margin: 0;
  padding: 0;
}

.suggestions-dropdown li {
  padding: 8px 12px;
  cursor: pointer;
}

.suggestions-dropdown li:hover {
  background-color: #f0f0f0;
}

.decision-section {
  background-color: #fafafa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #ddd;
}

.decision-options {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.decision-option {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.decision-option input[type="radio"] {
  margin-right: 8px;
}

.option-label {
  font-weight: bold;
  padding: 5px 12px;
  border-radius: 4px;
}

.approve-label {
  color: #155724;
}

.pending-label {
  color: #856404;
}

.obsolete-label {
  color: #721c24;
}

.input-group {
  margin-bottom: 15px;
}

.input-group label {
  display: block;
  font-weight: bold;
  margin-bottom: 5px;
}

.input-group textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  font-family: inherit;
}

.required {
  color: red;
}

.email-option {
  margin-bottom: 20px;
}

.email-option label {
  cursor: pointer;
}

.action-buttons {
  margin-top: 15px;
  display: flex;
  gap: 12px;
}

.submit-decision-btn {
  padding: 10px 30px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1em;
  transition: background-color 0.2s;
}

.submit-decision-btn:hover:not(:disabled) {
  background-color: #0056b3;
}

.submit-decision-btn:disabled {
  background-color: #ccc;
  color: #666;
  cursor: not-allowed;
}

.save-btn {
  background-color: #28a745;
}

.save-btn:hover:not(:disabled) {
  background-color: #218838;
}

.cancel-btn {
  padding: 10px 30px;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1em;
  transition: background-color 0.2s;
}

.cancel-btn:hover:not(:disabled) {
  background-color: #5a6268;
}

.success-message {
  color: #28a745;
  background-color: #dff0d8;
  padding: 10px;
  margin-top: 15px;
  border-radius: 5px;
}

.error-message {
  color: #dc3545;
  background-color: #f8d7da;
  padding: 10px;
  border-radius: 5px;
}

.loading-message {
  padding: 30px;
  text-align: center;
  color: #666;
}

@media (max-width: 768px) {
  .review-panel {
    width: 95%;
    padding: 15px;
  }

  .decision-options {
    flex-direction: column;
    gap: 10px;
  }

  .detail-table .label {
    width: auto;
  }

  .action-buttons {
    flex-direction: column;
  }
}
</style>
